import { useEffect, useState } from "react";
import { Plus, Mail, Phone, MapPin, Users } from "lucide-react";
import { adminBookings } from "@/lib/api";
import { STATUSES, statusMeta, fmtDate, EVENT_TYPES } from "./constants";

export default function BookingsList({ token, onEdit, refreshKey }) {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    adminBookings(token, filter === "all" ? {} : { status: filter }).then((d) => setBookings(d.bookings || [])).catch(() => {});
  }, [filter, token, refreshKey]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button data-testid="filter-all" onClick={() => setFilter("all")} className={`text-xs overline rounded-full px-4 py-2 border ${filter === "all" ? "bg-ocean-deep text-white border-ocean-deep" : "bg-white border-sand text-ocean hover:border-coral"}`}>All ({bookings.length})</button>
          {STATUSES.map((s) => (
            <button key={s.id} data-testid={`filter-${s.id}`} onClick={() => setFilter(s.id)}
              className={`text-xs overline rounded-full px-4 py-2 border ${filter === s.id ? "bg-ocean-deep text-white border-ocean-deep" : "bg-white border-sand text-ocean hover:border-coral"}`}>
              <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: s.dot }} /> {s.label}
            </button>
          ))}
        </div>
        <button data-testid="bl-new" onClick={() => onEdit({})} className="rounded-full bg-coral text-white px-5 py-2.5 text-xs overline hover:bg-[#c9633f] flex items-center gap-2"><Plus className="w-4 h-4" />New Booking</button>
      </div>

      {bookings.length === 0 && (
        <div className="rounded-3xl border border-dashed border-sand py-16 text-center text-ocean/50">No bookings yet.</div>
      )}
      <div className="space-y-3">
        {bookings.map((b) => {
          const s = statusMeta(b.status);
          return (
            <button key={b.id} data-testid="booking-row" onClick={() => onEdit(b)} className="w-full text-left rounded-2xl bg-white border border-sand hover:border-coral p-5 transition-colors grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              <div className="md:col-span-3">
                <p className="font-serif text-lg text-ocean-deep">{b.customer_name || "—"}</p>
                <p className="text-xs text-ocean/60 mt-0.5">{fmtDate(b.event_date)}</p>
                <span className={`inline-block mt-2 text-[10px] overline rounded-full px-3 py-1 ${s.bg} ${s.text}`}>{s.label}</span>
              </div>
              <div className="md:col-span-3 text-sm space-y-1">
                <p className="text-ocean-deep">{b.event_type}</p>
                <p className="text-xs text-ocean/60 flex items-center gap-1.5"><Users className="w-3 h-3" /> {b.guest_count} guests · {b.package_id}</p>
              </div>
              <div className="md:col-span-3 text-xs text-ocean/70 space-y-1">
                {b.customer_email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3 shrink-0" />{b.customer_email}</p>}
                {b.customer_phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3 shrink-0" />{b.customer_phone}</p>}
                {b.location && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3 shrink-0" />{b.location}</p>}
              </div>
              <div className="md:col-span-3 text-right">
                {b.total_estimate ? <p className="font-serif text-2xl text-coral">${Number(b.total_estimate).toLocaleString()}</p> : <p className="text-xs text-ocean/40">No total</p>}
                {b.add_ons?.length > 0 && <p className="text-[10px] text-ocean/50 mt-1">+ {b.add_ons.length} add-on{b.add_ons.length > 1 ? "s" : ""}</p>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
